'use client'

import { useEffect, useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { COUNTRY_CODES, type CountryCodeOption } from '@/constants/countryCodes'
import { cn } from '@/lib/utils'

type PhoneInputProps = {
  id?: string
  name?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  required?: boolean
  placeholder?: string
  inputClassName?: string
  selectTriggerClassName?: string
  error?: boolean
}

const DEFAULT_COUNTRY_OPTION: CountryCodeOption = COUNTRY_CODES.find(
  option => option.code === '+60'
) ??
  COUNTRY_CODES[0] ?? {
    iso2: 'MY',
    code: '+60',
    label: 'MY (+60)',
  }

const getCountryOptions = (): CountryCodeOption[] => COUNTRY_CODES

const digitsOnly = (value: string): string => value.replace(/\D/g, '')

const findCountryCodeMatch = (
  digits: string,
  options: CountryCodeOption[]
): CountryCodeOption | null => {
  if (!digits) return null
  const byLength = [...options].sort((a, b) => b.code.length - a.code.length)
  const matched = byLength.find(option =>
    digits.startsWith(option.code.replace(/\D/g, ''))
  )
  return matched ?? null
}

const formatWithDashes = (digits: string): string => {
  if (!digits) return ''
  if (digits.length <= 3) return digits
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

export function PhoneInput({
  id,
  name,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder,
  inputClassName,
  selectTriggerClassName,
  error = false,
}: PhoneInputProps) {
  const options = useMemo(() => getCountryOptions(), [])
  const [code, setCode] = useState(DEFAULT_COUNTRY_OPTION.code)
  const [number, setNumber] = useState('')

  useEffect(() => {
    const trimmed = value?.trim() ?? ''
    if (!trimmed) {
      setCode(DEFAULT_COUNTRY_OPTION.code)
      setNumber('')
      return
    }

    const valueDigits = digitsOnly(trimmed)
    const matched = findCountryCodeMatch(valueDigits, options)
    const matchedDigits = matched?.code.replace(/\D/g, '') ?? ''
    const shouldUseMatched =
      trimmed.startsWith('+') ||
      (matchedDigits.length > 0 &&
        valueDigits.length >= matchedDigits.length + 6)
    const resolved = shouldUseMatched
      ? (matched ?? DEFAULT_COUNTRY_OPTION)
      : DEFAULT_COUNTRY_OPTION
    const codeDigits = resolved.code.replace(/\D/g, '')

    const normalizedDigits = valueDigits.startsWith(codeDigits)
      ? valueDigits.slice(codeDigits.length)
      : valueDigits
    const nextNumber = normalizedDigits.replace(/^0+/, '')

    setCode(resolved.code)
    setNumber(nextNumber)
  }, [options, value])

  const updateValue = (nextCode: string, nextNumber: string) => {
    const digits = nextNumber.replace(/^0+/, '')
    const payload = digits.length > 0 ? `${nextCode}${digits}` : ''
    onChange(payload)
  }

  const handleCodeChange = (nextCode: string) => {
    setCode(nextCode)
    updateValue(nextCode, number)
  }

  const handleNumberChange = (nextValue: string) => {
    const clean = digitsOnly(nextValue).slice(0, 15)
    setNumber(clean)
    updateValue(code, clean)
  }

  return (
    <div className="flex gap-2">
      <Select value={code} onValueChange={handleCodeChange} disabled={disabled}>
        <SelectTrigger className={cn('w-32', selectTriggerClassName)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.code} value={option.code}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        name={name}
        type="tel"
        value={formatWithDashes(number)}
        onChange={event => handleNumberChange(event.target.value)}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete="tel"
        className={cn(error && 'border-red-300', inputClassName)}
      />
    </div>
  )
}
